import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaEllipsisH } from "react-icons/fa";

interface CustomDropdownProps {
  paymentLink: {
    isActive: boolean;
    reference: string;
    paymentLinkUrl: string;
  };
  handleActivateOrDeactivate: (isActive: boolean, reference: string) => void;
  handleDetailsClick: (paymentLink: any) => void;
  handleCopyLink: (link: string) => void;
  handleInitiatePayment: (link: string) => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  paymentLink,
  handleActivateOrDeactivate,
  handleDetailsClick,
  handleCopyLink,
  handleInitiatePayment,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Set dropdown position when opening
  const toggleDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top + window.scrollY + rect.height + 4, // Position below button
        left: rect.left + window.scrollX - 10, // Align with button
      });
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <>
      {/* Dropdown Button */}
      <button ref={buttonRef} onClick={toggleDropdown} className="p-2">
        <FaEllipsisH />
      </button>

      {/* Render Dropdown Outside of Table (Portal) */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="absolute z-50 bg-white shadow-lg rounded-md min-w-[150px] border"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <button
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
              onClick={() => {handleActivateOrDeactivate(paymentLink.isActive, paymentLink.reference); setIsOpen(false)}}
            >
              {paymentLink?.isActive ? "Deactivate Link" : "Activate Link"}
            </button>
            <button
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
              onClick={() =>{ handleDetailsClick(paymentLink); setIsOpen(false)}}
            >
              View Details
            </button>
            <button
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
              onClick={() => {handleCopyLink(paymentLink.paymentLinkUrl); setIsOpen(false)}}
            >
              Copy Link
            </button>
            <button
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
              onClick={() => {handleInitiatePayment(paymentLink.paymentLinkUrl); setIsOpen(false)}}
            >
              Initiate Payment
            </button>
          </div>,
          document.body // Render outside the table
        )}
    </>
  );
};

export default CustomDropdown;
